import { useLingui } from '@lingui/react/macro';
import { Stack } from '@mui/material';
import { TOAST_DURATION_CHOICES, TOAST_DURATION_OPTIONS } from '@/common/timeout_constants.ts';
import { useNotificationStore } from '@/feature/notifications';
import type { NotificationPosition } from '@/feature/notifications/components/toast_with_progress.tsx';
import { PreferenceSection } from './preference_section.tsx';
import { PreferenceSelect } from './preference_select.tsx';
import { PreferenceSwitch } from './preference_switch.tsx';

const notificationPositionOptions = [
  'top-right',
  'bottom-right',
  'top-center',
] as const satisfies readonly NotificationPosition[];

export function NotificationPreferencesSection() {
  const { t } = useLingui();
  const showToasts = useNotificationStore((state) => state.showToasts);
  const setShowToasts = useNotificationStore((state) => state.setShowToasts);
  const position = useNotificationStore((state) => state.position);
  const setPosition = useNotificationStore((state) => state.setPosition);
  const toastDuration = useNotificationStore((state) => state.toastDuration);
  const setToastDuration = useNotificationStore((state) => state.setToastDuration);

  const notificationsLabel = t({ comment: 'Preferences: notifications section title and toggle', message: 'Notifications' });
  const positionLabel = t({ comment: 'Preferences: toast position select', message: 'Position' });
  const durationLabel = t({ comment: 'Preferences: toast duration select', message: 'Notification duration' });

  const positionSelectOptions = notificationPositionOptions.map((option) => ({
    value: option,
    label:
      option === 'top-right'
        ? t({ comment: 'Preferences: toast position', message: 'Top Right' })
        : option === 'bottom-right'
          ? t({ comment: 'Preferences: toast position', message: 'Bottom Right' })
          : t({ comment: 'Preferences: toast position', message: 'Top Center' }),
  }));

  const durationSelectOptions = TOAST_DURATION_CHOICES.map((option) => ({
    value: option.value,
    label:
      option.value === 'fast'
        ? t({
            comment: 'Preferences: toast duration',
            message: `Fast (${{ seconds: TOAST_DURATION_OPTIONS.fast / 1000 }}s)`,
          })
        : option.value === 'slow'
          ? t({
              comment: 'Preferences: toast duration',
              message: `Slow (${{ seconds: TOAST_DURATION_OPTIONS.slow / 1000 }}s)`,
            })
          : t({ comment: 'Preferences: toast duration', message: 'Medium' }),
  }));

  return (
    <PreferenceSection title={notificationsLabel}>
      <Stack gap={1}>
        <PreferenceSwitch label={notificationsLabel} checked={showToasts} onChange={setShowToasts} />
        <PreferenceSelect
          id="notification-position"
          label={positionLabel}
          value={position}
          options={positionSelectOptions}
          onChange={setPosition}
          disabled={!showToasts}
        />
        <PreferenceSelect
          id="toast-duration"
          label={durationLabel}
          value={toastDuration}
          options={durationSelectOptions}
          onChange={setToastDuration}
          disabled={!showToasts}
        />
      </Stack>
    </PreferenceSection>
  );
}
